import React, { useEffect, useRef, useState } from "react";

/**
 * CursorGlow — a soft purple/teal radial glow that follows the cursor.
 * Hidden on touch devices. Intensifies near interactive elements.
 */
export const CursorGlow = () => {
  const dotRef = useRef(null);
  const haloRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    // only enable on devices with fine pointer
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(pointer: fine)");
    setEnabled(mq.matches);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let tx = 0, ty = 0, x = 0, y = 0;

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
      // detect interactive target
      const t = e.target;
      if (t && (t.closest && (t.closest("button, a, [data-spotlight], input, textarea, select, [role='button']")))) {
        setHover(true);
      } else {
        setHover(false);
      }
    };

    const tick = () => {
      x += (tx - x) * 0.18;
      y += (ty - y) * 0.18;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${tx - 4}px, ${ty - 4}px, 0)`;
      }
      if (haloRef.current) {
        haloRef.current.style.transform = `translate3d(${x - 120}px, ${y - 120}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={haloRef}
        className="pointer-events-none fixed top-0 left-0 z-[60] rounded-full will-change-transform"
        style={{
          width: 240,
          height: 240,
          background: hover
            ? "radial-gradient(closest-side, rgba(123,47,190,0.32), rgba(45,212,191,0.10) 50%, transparent 70%)"
            : "radial-gradient(closest-side, rgba(123,47,190,0.16), rgba(45,212,191,0.05) 55%, transparent 70%)",
          mixBlendMode: "screen",
          transition: "background 0.3s ease",
          filter: "blur(2px)",
        }}
        aria-hidden
        data-testid="cursor-halo"
      />
      <div
        ref={dotRef}
        className="pointer-events-none fixed top-0 left-0 z-[61] rounded-full will-change-transform"
        style={{
          width: 8,
          height: 8,
          background: hover ? "#2DD4BF" : "#7B2FBE",
          boxShadow: hover ? "0 0 14px rgba(45,212,191,0.8)" : "0 0 12px rgba(123,47,190,0.7)",
          transition: "background 0.2s ease",
        }}
        aria-hidden
      />
    </>
  );
};

export default CursorGlow;
